/**
 * v2Palette.js — Single source of truth for Canvas V2 node colors.
 *
 * Pulls from the Agentlets brand design system (`BRAND` + `ACCENT` + `S`
 * from components/common/GovernanceDesignSystem.jsx) so Canvas V2 shares a
 * visual language with Policy Library, Trust Center, Compliance Center,
 * Gateway, and every other governance surface.
 *
 * DESIGN INTENT
 * ─────────────
 *   • START is emerald — the workflow begins at a healthy green check.
 *   • END wears the Agentlets BRAND terracotta — the workflow's terminus is
 *     where the customer's value is delivered, so it's branded. This visually
 *     bookends every DAG with the Agentlets identity.
 *   • SWITCH   → amber   (routing / decisions)
 *   • FORK     → teal    (parallelism; static — known at def time)
 *   • DYN FORK → violet  (parallelism; dynamic — resolved at run time)
 *   • LOOP     → orange  (iteration)
 *   • SUBWF    → indigo  (call-out; nested workflow)
 *   • ASYNC SW → sky     (fire-and-forget; no return value)
 *   • TERMINATE→ rose    (abrupt end; failure variant)
 *   • LEAF     → stone   (default scalar task, neutral canvas)
 *
 * The palette is bright and saturated on purpose — Canvas V2's goal is to
 * delight customers on first glance. Every container tint keeps AA contrast
 * with its text (black/dark brand tone), and every border pops visibly
 * against the canvas background.
 */

import { BRAND, ACCENT, S } from '../../../components/common/GovernanceDesignSystem';

// ═══════════════════════════════════════════════════════════════════
//  Per-construct palette
// ═══════════════════════════════════════════════════════════════════
// Each construct exposes: bg, border, text, accent, softBg (container tint),
// chipBg / chipBorder / chipText for the top-left container tag.
// Values are bright/saturated so chrome reads as "designed," not "greyscale".

export const V2_PAL = Object.freeze({
  // ── Sentinels ──────────────────────────────────────────────────
  start: {
    bg:     '#10b981',          // emerald-500 — vibrant healthy green
    bgGrad: 'linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%)',
    text:   '#ffffff',
    ring:   'rgba(16, 185, 129, 0.25)',
  },
  end: {
    // Fix G53: END node was BRAND terracotta (red), which clashed with the
    // v2 design language where red is reserved for kill-switch + danger
    // states only. The END circle now wears the v2 sidebar navy — it
    // bookends every DAG with the platform's chrome identity (matching
    // the navy sidebar that grounds the white workspace) without
    // competing with semantic danger or with the green Start sentinel.
    bg:     '#0e1f3a',          // v2 sidebar navy
    bgGrad: 'linear-gradient(135deg, #16294a 0%, #0e1f3a 55%, #07142a 100%)',
    text:   '#ffffff',
    ring:   'rgba(14, 31, 58, 0.30)',
  },

  // ── Scalar leaf ────────────────────────────────────────────────
  leaf: {
    bg:      '#ffffff',
    border:  S[300],
    text:    S[900],
    typeTag: S[500],
    shadow:  '0 1px 3px rgba(15, 23, 42, 0.08)',
    hover:   BRAND.soft,
  },

  // ── Switch / routing (amber) ───────────────────────────────────
  switch: {
    head: {
      bg:     '#fffbeb',         // amber-50 (brighter than amber-100)
      border: '#f59e0b',         // amber-500 — solid, saturated
      text:   '#78350f',         // amber-900
      accent: '#92400e',
      shadow: '0 1px 3px rgba(245, 158, 11, 0.22)',
    },
    // Container chrome is intentionally neutral (cool slate) so the
    // colored switch HEAD pill carries all the "this is a switch" signal.
    // The amber outer wash was competing with the inner node colors and
    // made complex workflows feel visually loud. See design pass §2026-04.
    container: {
      bg:     'rgba(241, 245, 249, 0.7)',  // slate-100 @ 0.7
      border: '#cbd5e1',                    // slate-300 dashed
      chipBg: '#e2e8f0',                    // slate-200
      chipBorder: '#cbd5e1',                // slate-300
      chipText: '#475569',                  // slate-600
    },
    caseContainer: {
      bg:      'rgba(255, 255, 255, 0.78)',
      border:  '#cbd5e1',                   // slate-300 dotted
      chipBg:  '#ffffff',
      chipBorder: '#cbd5e1',                // slate-300
      chipText: '#334155',                  // slate-700
      defaultBg: 'rgba(248, 250, 252, 0.72)',
      defaultBorder: S[400],
      defaultChipBg: '#f8fafc',
      defaultChipText: S[700],
    },
  },

  // ── Static fork (teal — sibling of cyan, fits Agentlets palette) ─
  fork: {
    head: {
      bg:     '#f0fdfa',         // teal-50
      border: '#14b8a6',         // teal-500 — solid
      text:   '#115e59',         // teal-800
      accent: '#0f766e',
      shadow: '0 1px 3px rgba(20, 184, 166, 0.22)',
    },
    container: {
      bg:     'rgba(240, 253, 250, 0.55)',
      border: '#5eead4',         // teal-300 dashed
      chipBg: '#ccfbf1',
      chipBorder: '#14b8a6',
      chipText: '#115e59',
    },
    legContainer: {
      bg:     'rgba(255, 255, 255, 0.65)',
      border: '#99f6e4',         // teal-200 dotted
      chipBg: '#ffffff',
      chipBorder: '#5eead4',
      chipText: '#115e59',
    },
  },

  // ── Dynamic fork (violet — resolved at run time) ────────────────
  dynFork: {
    head: {
      bg:     '#f5f3ff',         // violet-50
      border: '#8b5cf6',         // violet-500
      text:   '#4c1d95',         // violet-900
      accent: '#6d28d9',         // violet-700
      shadow: '0 1px 3px rgba(139, 92, 246, 0.22)',
    },
    container: {
      bg:     'rgba(245, 243, 255, 0.55)',
      border: '#c4b5fd',         // violet-300 dashed
      chipBg: '#ede9fe',
      chipBorder: '#8b5cf6',
      chipText: '#5b21b6',
    },
    templateLeg: {
      stripeA: 'rgba(255, 255, 255, 0.6)',
      stripeB: 'rgba(237, 233, 254, 0.45)',
      border:  '#a78bfa',
      chipBg:  '#f5f3ff',
      chipBorder: '#a78bfa',
      chipText: '#6d28d9',
    },
  },

  // ── Join (echoes fork colour family, dynamic variant = violet) ──
  join: {
    staticBg:     '#f0fdfa',     // teal-50
    staticBorder: '#14b8a6',     // teal-500
    staticRing:   '#0d9488',     // teal-600 (badge circle)
    staticText:   '#115e59',
    dynamicBg:     '#f5f3ff',
    dynamicBorder: '#8b5cf6',
    dynamicRing:   '#7c3aed',
    dynamicText:   '#5b21b6',
  },

  // ── Loop / DoWhile (orange — iteration is "work repeats") ───────
  loop: {
    head: {
      bg:     '#fff7ed',         // orange-50
      border: '#f97316',         // orange-500 solid
      text:   '#7c2d12',         // orange-900
      accent: '#9a3412',
      shadow: '0 1px 3px rgba(249, 115, 22, 0.22)',
    },
    container: {
      bg:     'rgba(255, 247, 237, 0.55)',
      border: '#fdba74',         // orange-300 dashed
      chipBg: '#ffedd5',
      chipBorder: '#f97316',
      chipText: '#7c2d12',
    },
    bodyContainer: {
      bg:     'rgba(255, 255, 255, 0.65)',
      border: '#fed7aa',         // orange-200 dotted
      chipBg: '#ffffff',
      chipBorder: '#fdba74',
      chipText: '#7c2d12',
    },
    // The self-loop back-edge that shows "iteration continues"
    backEdge: '#ea580c',         // orange-600
  },

  // ── SubWorkflow (indigo = sync; sky = async fire-and-forget) ────
  subWorkflow: {
    sync: {
      bg:     '#eef2ff',         // indigo-50
      border: '#6366f1',         // indigo-500 solid
      text:   '#3730a3',         // indigo-800
      accent: '#4338ca',
      shadow: '0 1px 3px rgba(99, 102, 241, 0.22)',
    },
    async: {
      bg:     '#f0f9ff',         // sky-50
      border: '#0ea5e9',         // sky-500 solid
      text:   '#0c4a6e',         // sky-900
      accent: '#0369a1',
      shadow: '0 1px 3px rgba(14, 165, 233, 0.22)',
    },
    container: {
      bg:     'rgba(238, 242, 255, 0.55)',
      border: '#a5b4fc',         // indigo-300
      chipBg: '#e0e7ff',
      chipBorder: '#6366f1',
      chipText: '#3730a3',
    },
  },

  // ── Terminate (rose on failure, slate on success) ───────────────
  terminate: {
    failed: {
      bg:     ACCENT.rose.bg,
      border: '#f43f5e',         // rose-500
      text:   '#881337',         // rose-900
      accent: '#9f1239',
    },
    completed: {
      bg:     ACCENT.slate.bg,
      border: S[400],
      text:   S[900],
      accent: S[700],
    },
  },

  // ── Convergence dot (neutral) ──────────────────────────────────
  meet: {
    bg:     S[500],              // stone-500
    border: S[200],              // soft ring
  },

  // ── Edges (semantic variants) ──────────────────────────────────
  edge: {
    trunk:       { stroke: '#475569', strokeWidth: 1.75 },           // slate-600 — more present
    branch:      { stroke: '#475569', strokeWidth: 1.75 },
    convergence: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '4 4' },
    // Loop back-edge (orange, curved)
    loopBack:    { stroke: '#ea580c', strokeWidth: 1.75, strokeDasharray: '5 3' },
  },

  // ── Execution overlay (applied on top of base palette) ─────────
  exec: {
    running:   { ring: '#0ea5e9', glow: 'rgba(14, 165, 233, 0.35)', label: 'RUNNING' },
    completed: { ring: '#10b981', glow: 'rgba(16, 185, 129, 0.35)', label: 'DONE' },
    failed:    { ring: '#f43f5e', glow: 'rgba(244, 63, 94, 0.35)',  label: 'FAILED' },
    skipped:   { ring: S[400],    glow: 'rgba(168, 162, 158, 0.30)', label: 'SKIPPED' },
    waiting:   { ring: '#a855f7', glow: 'rgba(168, 85, 247, 0.30)',  label: 'WAITING' },
  },

  // ── Canvas background / chrome ─────────────────────────────────
  canvas: {
    // Clean Agentlets white — the brand-tinted wash clashed with the
    // construct chrome tints (amber switch / teal fork / orange loop),
    // especially on busy workflows like complex_claim_stress_test where
    // the salmon backdrop was fighting the yellow switch containers.
    // Containers carry the brand tint; the canvas stays neutral.
    background:   '#ffffff',
    backgroundDots: S[200],
    panelBg:      '#ffffff',
    panelBorder:  S[200],
    panelMuted:   S[500],
  },
});

/**
 * Brand tokens surfaced so callers don't need to import GovernanceDesignSystem
 * themselves. Prefer `V2_PAL` for per-construct colors; use these for chrome.
 */
export const V2_BRAND = BRAND;
export const V2_STONE = S;
export const V2_ACCENT = ACCENT;
